import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { fmtMoney, shortDate } from "../lib/format";
import { toast } from "sonner";
import { ArrowLeft, Trash2, Copy, CheckCircle2, ShieldCheck, Sparkles } from "lucide-react";
import HelpButton from "../components/HelpButton";
import { useAccounts } from "../lib/accounts";

// A txn is "NEW" for the ribbon if its created_at is within the last N hours.
// Kept at 24h so the ribbon reliably catches next-morning audits without
// showing forever on old rows the user has already seen.
const NEW_WINDOW_HOURS = 24;

function isTxnNew(t) {
  if (!t?.created_at) return false;
  try {
    const created = new Date(t.created_at);
    const ageHours = (Date.now() - created.getTime()) / 36e5;
    return ageHours >= 0 && ageHours < NEW_WINDOW_HOURS;
  } catch {
    return false;
  }
}

// Groups are "NEW" if ANY transaction inside was created recently.
function groupHasNew(g) {
  return (g?.transactions || []).some(isTxnNew);
}

export default function Duplicates() {
  const { accounts, selectedId } = useAccounts();
  const [data, setData] = useState(null);
  const [keep, setKeep] = useState({}); // groupKey -> txn id to keep
  const [busy, setBusy] = useState(false);
  const [fuzzy, setFuzzy] = useState(true);

  const currentAccount = accounts.find((a) => a.id === selectedId) || accounts[0];

  const load = useCallback(async () => {
    if (!currentAccount?.id) return;
    const params = new URLSearchParams({
      account_id: currentAccount.id,
      fuzzy: fuzzy ? "1" : "0",
    });
    const { data } = await api.get(`/duplicates?${params.toString()}`);
    setData(data);
    // default: keep the first (oldest) in each group
    const initial = {};
    data.groups.forEach((g) => {
      const key = `${g.type}-${g.amount}-${g.date}`;
      initial[key] = g.transactions[0].id;
    });
    setKeep(initial);
  }, [currentAccount?.id, fuzzy]);

  useEffect(() => {
    load();
  }, [load]);

  const deleteGroup = async (g) => {
    const key = `${g.type}-${g.amount}-${g.date}`;
    const keepId = keep[key];
    const toDelete = g.transactions.filter((t) => t.id !== keepId).map((t) => t.id);
    if (!toDelete.length) return;
    if (!window.confirm(`Delete ${toDelete.length} duplicate${toDelete.length === 1 ? "" : "s"}?`))
      return;
    setBusy(true);
    try {
      const { data } = await api.post("/transactions/bulk-delete", { ids: toDelete });
      toast.success(`Deleted ${data.deleted} duplicate${data.deleted === 1 ? "" : "s"}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  const deleteAllExtras = async () => {
    if (!data) return;
    const toDelete = [];
    data.groups.forEach((g) => {
      const key = `${g.type}-${g.amount}-${g.date}`;
      const keepId = keep[key];
      g.transactions.forEach((t) => {
        if (t.id !== keepId) toDelete.push(t.id);
      });
    });
    if (!toDelete.length) return;
    if (!window.confirm(`Delete ${toDelete.length} duplicate transactions across all groups?`))
      return;
    setBusy(true);
    try {
      const { data: res } = await api.post("/transactions/bulk-delete", { ids: toDelete });
      toast.success(`Deleted ${res.deleted} duplicates`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  const markNotDup = async (g) => {
    const ids = g.transactions.map((t) => t.id);
    setBusy(true);
    try {
      const { data: res } = await api.post("/duplicates/mark-not-duplicate", { ids });
      toast.success(
        `Marked ${res.updated} row${res.updated === 1 ? "" : "s"} as not a duplicate`
      );
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't mark");
    } finally {
      setBusy(false);
    }
  };

  if (!data)
    return (
      <div className="p-6">
        <div className="nb-tag">Loading…</div>
      </div>
    );

  // Reorder: freshly-added groups float to the top so audit sessions see
  // newly-added-but-suspect items immediately.
  const orderedGroups = [...(data.groups || [])].sort((a, b) => {
    const aNew = groupHasNew(a) ? 1 : 0;
    const bNew = groupHasNew(b) ? 1 : 0;
    return bNew - aNew; // NEW groups first, otherwise preserve backend order
  });
  const newGroupCount = orderedGroups.filter(groupHasNew).length;

  return (
    <div className="space-y-4" data-testid="duplicates-page">
      <div className="flex items-center justify-between">
        <Link to="/register" className="nb-btn nb-btn-secondary !py-2 !px-3">
          <ArrowLeft size={16} strokeWidth={3} /> Back
        </Link>
        <h1 className="font-display text-2xl font-black">Duplicate Inbox</h1>
        <HelpButton id="duplicates" />
      </div>

      {newGroupCount > 0 && (
        <div
          className="nb-card p-3 flex items-center gap-2"
          style={{ background: "#b5e4ca" }}
          data-testid="new-summary-banner"
        >
          <Sparkles size={16} strokeWidth={3} />
          <div className="text-sm font-bold">
            {newGroupCount} group{newGroupCount === 1 ? "" : "s"} with fresh
            duplicates from the last {NEW_WINDOW_HOURS}h — floated to the top
            for easy review.
          </div>
        </div>
      )}

      <div className="nb-card p-4">
        <div className="flex items-center gap-2 mb-1">
          <Copy size={18} strokeWidth={3} />
          <div className="font-display font-black text-lg">
            {data.total_groups} group{data.total_groups === 1 ? "" : "s"} · {data.total_extra} extra
          </div>
        </div>
        <div className="text-sm text-[#4a4a4a] font-medium">
          Scanning <b>{currentAccount?.name || "this account"}</b> only. Change accounts from the top
          switcher to scan elsewhere.
        </div>
        <div className="flex items-center gap-2 mt-2">
          <label className="flex items-center gap-2 text-xs font-bold cursor-pointer">
            <input
              data-testid="dup-fuzzy-toggle"
              type="checkbox"
              checked={fuzzy}
              onChange={(e) => setFuzzy(e.target.checked)}
              className="w-4 h-4"
            />
            Fuzzy match (±7 days &amp; similar payees)
          </label>
        </div>
        {data.total_extra > 0 && (
          <button
            data-testid="delete-all-btn"
            disabled={busy}
            onClick={deleteAllExtras}
            className="nb-btn nb-btn-danger w-full mt-3 !py-3"
          >
            <Trash2 size={16} strokeWidth={3} /> Delete all {data.total_extra} duplicates
          </button>
        )}
      </div>

      {data.groups.length === 0 && (
        <div className="nb-card p-8 text-center">
          <CheckCircle2 size={40} strokeWidth={2.5} className="mx-auto mb-2 text-[#12854a]" />
          <div className="font-display font-black text-xl">No duplicates.</div>
          <div className="text-sm text-[#4a4a4a] font-medium mt-1">Your register is clean.</div>
        </div>
      )}

      {orderedGroups.map((g) => {
        const key = `${g.type}-${g.amount}-${g.date}`;
        const hasNew = groupHasNew(g);
        return (
          <div
            key={key}
            className={`nb-card p-4 ${hasNew ? "ring-2 ring-[#12854a]/40" : ""}`}
            data-testid={`dup-group-${key}`}
            data-new={hasNew ? "1" : "0"}
            style={hasNew ? { background: "#e6f5ec" } : undefined}
          >
            <div className="flex items-baseline justify-between mb-2">
              <div>
                <div className="font-display font-black text-lg flex items-center gap-2">
                  {g.type === "deposit" ? "+" : "-"}
                  {fmtMoney(g.amount)}
                  {hasNew && (
                    <span
                      data-testid={`dup-group-new-badge-${key}`}
                      className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#12854a] text-white"
                    >
                      <Sparkles size={10} strokeWidth={3} />
                      New
                    </span>
                  )}
                </div>
                <div className="text-xs font-bold text-[#4a4a4a]">
                  {shortDate(g.date)} · {g.count} copies
                </div>
              </div>
              <button
                data-testid={`delete-group-${key}`}
                disabled={busy}
                onClick={() => deleteGroup(g)}
                className="nb-btn nb-btn-danger !py-2 !px-3 !text-xs"
              >
                <Trash2 size={14} strokeWidth={3} /> Delete {g.count - 1}
              </button>
            </div>
            <div className="flex justify-end mb-2">
              <button
                data-testid={`not-dup-group-${key}`}
                disabled={busy}
                onClick={() => markNotDup(g)}
                title="These aren't actually duplicates. Never flag this group again."
                className="nb-btn nb-btn-secondary !py-1.5 !px-2.5 !text-[11px]"
              >
                <ShieldCheck size={12} strokeWidth={3} /> Not a duplicate
              </button>
            </div>
            <div className="space-y-1.5">
              {g.transactions.map((t) => {
                const txnNew = isTxnNew(t);
                return (
                <label
                  key={t.id}
                  data-testid={`dup-txn-${t.id}`}
                  data-new={txnNew ? "1" : "0"}
                  className="flex items-center gap-3 p-2 rounded-lg border-2 border-[#1a1a1a] cursor-pointer"
                  style={{ background: keep[key] === t.id ? "#b5e4ca" : "#fff" }}
                >
                  <input
                    type="radio"
                    name={key}
                    checked={keep[key] === t.id}
                    onChange={() => setKeep({ ...keep, [key]: t.id })}
                    className="w-4 h-4"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm truncate flex items-center gap-1.5">
                      {t.payee}
                      {txnNew && (
                        <span
                          data-testid={`dup-txn-new-badge-${t.id}`}
                          className="inline-flex items-center gap-0.5 text-[8px] font-black uppercase tracking-wider px-1 py-0.5 rounded bg-[#12854a] text-white shrink-0"
                          title={`Added in the last ${NEW_WINDOW_HOURS}h`}
                        >
                          NEW
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-[#4a4a4a] font-medium">
                      {t.category || "Other"}
                      {t.cleared ? " · cleared" : " · uncleared"}
                      {t.memo ? ` · ${t.memo}` : ""}
                    </div>
                  </div>
                  {keep[key] === t.id && (
                    <span className="nb-tag !text-[9px] !py-0 !px-2 bg-white">KEEP</span>
                  )}
                </label>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
